import { inject, Injectable, InjectionToken } from '@angular/core';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  skip,
  startWith,
  Subject,
  switchMap,
  timer,
} from 'rxjs';
import { ConversationFilters, InboxFilters } from '../conversation-filters';
import { FirestoreService } from '../firestore.service';
import { ConversationDetail } from '../interface/conversation.interface';
import {
  ConversationChannel,
  GetMultiConversationDetailsV2ResponseDetailedConversation,
} from '@vendasta/conversation';
import { ConversationStatelessService } from '../conversation-stateless.service';
import type {
  ListConversationsResponseInboxConversation,
  ListConversationsResponseSubjectContact,
} from '@vendasta/protos/conversation/v1';

export interface FilteredListParams {
  accountGroupId: string;
  participantId: string;
  organizationId: string;
  filters: ConversationFilters | null;
  inboxFilters: Omit<InboxFilters, 'assignedToMe' | 'unassigned'> | null;
  assigneeUserId: string;
  filterTerm: string;
  groupId: string | null | undefined;
}

export interface ConversationListState {
  conversations: ConversationDetail[] | null;
  loading: boolean;
  empty: boolean | null;
}

/**
 * Slim trigger-only shape backed by a ConversationLookup Firestore doc.
 * The change stream only drives "refetch now" decisions — all conversation
 * data including read status and timestamps comes from the RPC.
 */
export interface ConversationChangeEvent {
  conversationId: string;
  scopeId?: string;
  latestRelevantActivityTime?: { toMillis(): number };
}

interface Action {
  type: 'loadMore' | 'refresh';
  silent?: boolean;
}

/** Max consecutive empty pages before giving up (guards against infinite cursor loops). */
const MAX_EMPTY_PAGES = 5;

export const CHANGE_STREAM_DEBOUNCE = new InjectionToken<number>(
  'ConversationPageFetcherService change stream debounce (ms)',
  { providedIn: 'root', factory: () => 2_000 },
);

@Injectable()
export class ConversationPageFetcherService {
  private readonly statelessService = inject(ConversationStatelessService);
  private readonly firestoreService = inject(FirestoreService);
  private readonly debounceMs = inject(CHANGE_STREAM_DEBOUNCE);

  private readonly simulatedChanges$$ = new Subject<ConversationChangeEvent[]>();

  /** Inject a simulated Firestore change event for testing. */
  simulateChange(docs: ConversationChangeEvent[]): void {
    this.simulatedChanges$$.next(docs);
  }

  /**
   * Returns an observable of conversation list state driven by user actions
   * (load more, refresh) and real-time Firestore change detection.
   *
   * Pagination state (`nextCursor`, `accumulated`) is held in closure variables.
   * This is safe because the outer `switchMap` cancels previous inner subscriptions
   * before starting a new one — only one fetch chain is active at a time.
   */
  listConversations$(
    params: FilteredListParams,
    loadMore$: Observable<void>,
    refresh$: Observable<void>,
  ): Observable<ConversationListState> {
    let nextCursor = '';
    let accumulated: ConversationDetail[] = [];

    const fetchPage = (depth = 0): Observable<ConversationListState> =>
      this.fetchPage(params, nextCursor).pipe(
        switchMap((result) => {
          nextCursor = result.nextCursor;
          accumulated = [...accumulated, ...result.mapped];
          if (accumulated.length === 0 && nextCursor && depth < MAX_EMPTY_PAGES) {
            return fetchPage(depth + 1);
          }
          return of({
            conversations: accumulated,
            loading: false,
            empty: accumulated.length === 0,
          } as ConversationListState);
        }),
      );

    const { organizationId, groupId } = params;
    const sharedChangeStream$ = this.combinedChangeStream$(groupId ?? organizationId).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const actions$ = this.buildActions$(loadMore$, refresh$, sharedChangeStream$);

    return actions$.pipe(
      filter((action) => {
        if (action.type === 'loadMore' && accumulated.length > 0 && !nextCursor) {
          return false;
        }
        return true;
      }),
      switchMap((action) => {
        if (action.type === 'refresh') {
          nextCursor = '';
          accumulated = [];
        }
        if (action.silent) {
          return fetchPage();
        }
        return fetchPage().pipe(
          startWith({
            conversations: null,
            loading: true,
            empty: null,
          } as ConversationListState),
        );
      }),
    );
  }

  private buildActions$(
    loadMore$: Observable<void>,
    refresh$: Observable<void>,
    changeStream$: Observable<ConversationChangeEvent[]>,
  ): Observable<Action> {
    const load$ = loadMore$.pipe(
      map((): Action => ({ type: 'loadMore' })),
    );
    const auto$ = timer(60_000, 60_000).pipe(
      map((): Action => ({ type: 'refresh', silent: true })),
    );
    const manual$ = refresh$.pipe(
      map((): Action => ({ type: 'refresh' })),
    );
    const changes$ = changeStream$.pipe(
      map((docs) => this.fingerprint(docs)),
      distinctUntilChanged(),
      skip(1),
      debounceTime(this.debounceMs),
      map((): Action => ({ type: 'refresh' })),
    );

    return merge(load$, auto$, manual$, changes$).pipe(
      startWith({ type: 'loadMore' } as Action),
    );
  }

  private combinedChangeStream$(scopeId: string): Observable<ConversationChangeEvent[]> {
    // Cast is safe: FirestoreConversationLookup is a structural superset of
    // ConversationChangeEvent (adds optional `id`, narrows Timestamp to a
    // shape with toMillis()).
    const realtime$ = this.firestoreService.watchLookupByScopeId$(scopeId, 50) as Observable<ConversationChangeEvent[]>;
    return merge(realtime$, this.simulatedChanges$$);
  }

  private fingerprint(docs: ConversationChangeEvent[]): string {
    return docs
      .map((d) => `${d.conversationId}:${d.latestRelevantActivityTime?.toMillis() ?? 0}`)
      .sort()
      .join(',');
  }

  /**
   * Fetches a single page from the backend.
   *
   * The enriched ListConversations response carries the full
   * VStore Conversation (read status, timestamps, assignee) plus
   * denormalized preview and contact data from ConversationLookup.
   * No follow-up GetMultiConversationDetailsV2 call needed.
   */
  private fetchPage(
    params: FilteredListParams,
    cursor: string,
  ): Observable<{
    mapped: ConversationDetail[];
    nextCursor: string;
  }> {
    const {
      accountGroupId,
      participantId,
      inboxFilters,
      filters,
      assigneeUserId,
    } = params;
    return this.statelessService
      .listConversations({
        accountGroupId,
        participantId,
        pageSize: 20,
        cursor,
        channels: (
          inboxFilters?.channels ?? []
        ) as ConversationChannel[],
        isOpen: filters?.isOpen,
        isAnonymous: filters?.isAnonymous,
        isSpam: filters?.isSpam,
        unreadOnly: inboxFilters?.unreadOnly ?? false,
        awaitingResponseOnly:
          inboxFilters?.awaitingResponse ?? false,
        assigneeUserId,
        searchTerm: params.filterTerm,
      })
      .pipe(
        map((result) => ({
          mapped: result.conversations
            .map((ic) => toConversationDetail(ic))
            .filter((d): d is ConversationDetail =>
              !!d?.conversation),
          nextCursor: result.nextCursor,
        })),
      );
  }
}

function toConversationDetail(
  ic: ListConversationsResponseInboxConversation,
): ConversationDetail {
  const participants = [
    ...participantsFromContact(
      ic.subjectContact, ic.conversation?.channel,
    ),
    ...senderParticipant(ic),
  ];
  const detail =
    GetMultiConversationDetailsV2ResponseDetailedConversation
      .fromProto({
        conversation: ic.conversation,
        message: ic.latestMessage,
        event: ic.latestEvent,
        participants,
        summary: '',
      });
  return Object.assign(detail, {
    senderIsSelf: ic.latestActivitySenderIsSelf || undefined,
  });
}

function senderParticipant(
  ic: ListConversationsResponseInboxConversation,
): object[] {
  if (
    !ic.latestMessage?.participantId ||
    !ic.latestActivitySenderName
  ) {
    return [];
  }
  return [{
    participantId: ic.latestMessage.participantId,
    name: ic.latestActivitySenderName,
    isSubjectParticipant: false,
  }];
}

function participantsFromContact(
  sc: ListConversationsResponseSubjectContact | undefined,
  channel?: string,
): object[] {
  if (!sc?.name && !sc?.email && !sc?.phone) {
    return [];
  }
  // INTERNAL = "Your Expert" (business-to-partner); subject is the partner.
  const participantType =
    channel === 'CONVERSATION_CHANNEL_INTERNAL'
      ? 'PARTICIPANT_TYPE_PARTNER'
      : 'PARTICIPANT_TYPE_CUSTOMER';
  return [
    {
      name: sc.name ?? '',
      email: sc.email ?? '',
      phoneNumber: sc.phone ?? '',
      profileImageUrl: sc.profileImageUrl ?? '',
      isSubjectParticipant: true,
      participantType,
    },
  ];
}
